// ── clock formatting ─────────────────────────────────────────────────────────
//
// THE house rule: innernet never shows military time. Every wall clock the
// product renders is 12-hour with am/pm, lowercase (law_lowercase).
//
// Before this module every hour was hand-rolled with `padStart(2, '0')`, which
// is exactly how 24-hour output crept in. Import from here instead — including
// in prompt packets, because the model echoes the format it is given.
//
// Casing: "2:30 pm", "11 am", "12 noon", "12 midnight". Never "14:30", never
// "2:30 PM".

/** A bare hour bucket (0–23) as a short label: "6 am", "12 noon", "11 pm". */
export function fmtHour(hour: number): string {
  const h = ((Math.round(hour) % 24) + 24) % 24;
  if (h === 0) return '12 midnight';
  if (h === 12) return '12 noon';
  const suffix = h < 12 ? 'am' : 'pm';
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve} ${suffix}`;
}

/** The compact axis form for charts, where space is tight: "6a", "12p", "11p". */
export function fmtHourTick(hour: number): string {
  const h = ((Math.round(hour) % 24) + 24) % 24;
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve}${h < 12 ? 'a' : 'p'}`;
}

/** A full clock time from an instant: "2:30 pm". Minutes drop when they're :00. */
export function fmtClock(iso: string | number | Date | null | undefined, timeZone?: string): string {
  if (iso === null || iso === undefined || iso === '') return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = get('dayPeriod').toLowerCase();
  return minute === '00' ? `${hour} ${dayPeriod}` : `${hour}:${minute} ${dayPeriod}`;
}

/** Date + clock, for a row that needs both: "25 jul, 2:30 pm". */
export function fmtDateClock(iso: string | number | Date | null | undefined, timeZone?: string): string {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(timeZone ? { timeZone } : {}),
  })
    .format(d)
    .toLowerCase()
    // ICU's en-GB short month for September is "Sept"; the house writes "sep"
    .replace(/\bsept\b/, 'sep');
  return `${date}, ${fmtClock(d, timeZone)}`;
}

/** An hour RANGE, for describing a window: "10 pm to 2 am". */
export function fmtHourRange(from: number, to: number): string {
  return `${fmtHour(from)} to ${fmtHour(to)}`;
}

/**
 * The viewer's IANA timezone, or null when it can't be determined.
 * Client-only — the server has no business guessing a person's clock.
 */
export function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * Which hour bucket (0–23) an instant falls in FOR A GIVEN ZONE.
 * Rhythm buckets must be built in the person's own clock — an hour histogram in
 * UTC makes "you think best after ten" false for everyone outside London, and
 * makes am/pm meaningless.
 */
export function hourInZone(iso: string | number | Date, timeZone?: string | null): number {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  if (!timeZone) return d.getUTCHours();
  try {
    const h = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: false,
      timeZone,
    }).format(d);
    const n = parseInt(h, 10);
    return Number.isFinite(n) ? n % 24 : d.getUTCHours();
  } catch {
    return d.getUTCHours();
  }
}

/** Weekday index (0 = sunday) in a given zone. */
export function weekdayInZone(iso: string | number | Date, timeZone?: string | null): number {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  if (!timeZone) return d.getUTCDay();
  try {
    const name = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone }).format(d);
    const idx = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(name);
    return idx >= 0 ? idx : d.getUTCDay();
  } catch {
    return d.getUTCDay();
  }
}
